import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import {memo, useCallback, useMemo, useState} from 'react'
import {Controller, useFieldArray, useFormContext, UseFieldArrayRemove} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {createEmptyKpiDefinition} from '../../mappers'
import {KpiEditDefinitionFormValues, KpiEditFormValues} from '../../types'

import {EditKpiRow} from './EditKpiRow'

type EditKpiGroupAccordionProps = {
  tabIndex: number
  groupIndex: number
  definitionCount: number
  groupName: string // Pass initial name to avoid useWatch when collapsed
  definitionNames: string[] // Pass definition names for search filtering without useWatch
  defaultExpanded?: boolean
  searchTerm?: string
  onDeleteGroup: () => void
  plantId: string
}

// Separate component for accordion content - only mounts when expanded
const AccordionContentComponent = ({
  tabIndex,
  groupIndex,
  searchTerm,
  groupName,
  fields,
  remove,
  plantId
}: {
  tabIndex: number
  groupIndex: number
  searchTerm: string
  groupName: string
  fields: (KpiEditDefinitionFormValues & {id: string})[]
  remove: UseFieldArrayRemove
  plantId: string
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const normalizedSearch = useMemo(() => searchTerm.toLowerCase().trim(), [searchTerm])

  const visibleDefinitionIndices = useMemo(() => {
    return fields
      .map((field, idx) => ({field, idx}))
      .filter(({field}) => {
        if (!normalizedSearch) return true
        return (
          groupName?.toLowerCase().includes(normalizedSearch) ||
          field.name.toLowerCase().includes(normalizedSearch)
        )
      })
      .map(({idx}) => idx)
  }, [fields, normalizedSearch, groupName])

  return (
    <Box>
      {visibleDefinitionIndices.map((definitionIndex) => (
        <EditKpiRow
          key={fields[definitionIndex]?.id ?? definitionIndex}
          tabIndex={tabIndex}
          groupIndex={groupIndex}
          definitionIndex={definitionIndex}
          onDelete={() => remove(definitionIndex)}
          plantId={plantId}
        />
      ))}
      {visibleDefinitionIndices.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{py: 2, textAlign: 'center'}}>
          {t('editKpisModal.noKpisInGroup')}
        </Typography>
      )}
    </Box>
  )
}

const AccordionContent = memo(AccordionContentComponent)

const EditKpiGroupAccordionComponent = ({
  tabIndex,
  groupIndex,
  definitionCount,
  groupName: initialGroupName,
  definitionNames,
  defaultExpanded = false,
  searchTerm = '',
  onDeleteGroup,
  plantId
}: EditKpiGroupAccordionProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {control} = useFormContext<KpiEditFormValues>()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const basePath = useMemo(
    () => `tabGroups.${tabIndex}.groups.${groupIndex}` as const,
    [tabIndex, groupIndex]
  )

  const {
    fields: definitionFields,
    append: appendDefinition,
    remove: removeDefinition
  } = useFieldArray({
    control,
    name: `${basePath}.definitions`
  })

  const handleAddDefinition = useCallback(() => {
    appendDefinition(createEmptyKpiDefinition(null))
    setIsExpanded(true)
  }, [appendDefinition])

  // Use initial group name for filtering - no useWatch needed
  const normalizedSearch = useMemo(() => searchTerm.toLowerCase().trim(), [searchTerm])

  // Show group if group name matches OR any definition name matches
  const shouldShowGroup = useMemo(
    () =>
      !normalizedSearch ||
      initialGroupName?.toLowerCase().includes(normalizedSearch) ||
      definitionNames.some((name) => name.toLowerCase().includes(normalizedSearch)),
    [normalizedSearch, initialGroupName, definitionNames]
  )

  if (!shouldShowGroup) {
    return null
  }

  return (
    <Accordion
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
      TransitionProps={{unmountOnExit: true}}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between'
          }
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{flex: 1}}>
          <Controller
            key={`${basePath}.name`}
            name={`${basePath}.name`}
            control={control}
            render={({field, fieldState}) => (
              <TextField
                {...field}
                size="small"
                placeholder={t('editKpisModal.groupNamePlaceholder')}
                error={!!fieldState.error}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                sx={{minWidth: 200}}
              />
            )}
          />
          <Typography variant="body2" color="text.secondary">
            ({definitionCount})
          </Typography>
          <Box sx={{flex: 1}} />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              size="small"
              startIcon={<AddIcon />}
              variant="text"
              onClick={handleAddDefinition}
              data-cy="add-definition-button"
            >
              {t('editKpisModal.action.addDefinition')}
            </Button>
            <Tooltip title={t('editKpisModal.action.deleteGroup')}>
              <IconButton
                size="small"
                color="error"
                onClick={onDeleteGroup}
                aria-label={t('editKpisModal.action.deleteGroup')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <AccordionContent
          tabIndex={tabIndex}
          groupIndex={groupIndex}
          searchTerm={searchTerm}
          groupName={initialGroupName}
          fields={definitionFields}
          remove={removeDefinition}
          plantId={plantId}
        />
      </AccordionDetails>
    </Accordion>
  )
}

export const EditKpiGroupAccordion = memo(EditKpiGroupAccordionComponent)
